"use client";

import { api } from "@/shared/api/api";
import { BRANDS_API_PATHS } from "../constants/apiConstants";

export async function getBrands<T = unknown>() {
  return (await api.get<T[]>(BRANDS_API_PATHS.brands)) ?? [];
}

export async function getTopDealBrands<T = unknown>() {
  return (await api.get<T[]>(BRANDS_API_PATHS.topDeals)) ?? [];
}

function isBrandProductsEnvelope<T, M>(
  value: unknown,
): value is { data: T[]; meta?: M } {
  return (
    typeof value === "object" &&
    value !== null &&
    "data" in value &&
    Array.isArray((value as { data: unknown }).data)
  );
}

export async function getBrandProducts<T = unknown, M = unknown>(
  brandSlug: string,
  page: number,
  limit: number,
) {
  // api.get unwraps { success, statusCode, data } to either `data` (array) or { data, meta } when meta exists
  const raw = await api.get<T[] | { data: T[]; meta?: M }>(
    BRANDS_API_PATHS.brandProducts(brandSlug, page, limit),
  );

  const items = Array.isArray(raw) ? raw : isBrandProductsEnvelope<T, M>(raw) ? raw.data : [];

  const metaFromResponse =
    !Array.isArray(raw) && isBrandProductsEnvelope<T, M>(raw) && raw.meta !== undefined
      ? raw.meta
      : undefined;

  if (items.length > limit) {
    const offset = Math.max(0, (page - 1) * limit);
    return {
      data: items.slice(offset, offset + limit),
      meta: {
        ...(typeof metaFromResponse === "object" && metaFromResponse ? metaFromResponse : ({} as M)),
        total: items.length,
      } as M,
    };
  }

  return {
    data: items,
    meta: {
      ...(typeof metaFromResponse === "object" && metaFromResponse ? metaFromResponse : ({} as M)),
      total:
        metaFromResponse !== undefined &&
        typeof metaFromResponse === "object" &&
        metaFromResponse !== null &&
        "total" in metaFromResponse &&
        typeof (metaFromResponse as { total?: number }).total === "number"
          ? (metaFromResponse as { total: number }).total
          : items.length,
    } as M,
  };
}
